import '../professions/professions.css'
import { LocationWindow } from '../LocationWindow'
import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import type { Attributes, Hero, InventoryEntry, Loadout, LoadoutSlot, Slot } from '@mezha/api'
import {
  useAllocateMutation, useEquipMutation, useHeroQuery, useInventoryQuery, useLoadoutQuery, useResetAttributesMutation, useUnequipMutation,
} from '../../api/endpoints/hero'
import { useFaithQuery } from '../../api/endpoints/social'
import { useCommand } from '../../lib/useCommand'
import { usePhone } from '../../lib/useSize'
import { useShellMode } from '../../shell/ShellContext'
import { useAppDispatch } from '../../app/store'
import { ui } from '../../app/uiSlice'
import { Emblem } from '../../ui/brand/Emblems'
import { Failure, Loading } from '../../ui/State'
import { ActionButton, ConfirmInPlace, Gem, PlankButton, PriceButton, Sheet, cx, rarityClass } from '../../ui'
import { ATTRS, DERIVED_RU, ItemArt, ResourceBonusNote, SLOTS, gearFacts, modDiff, modList, pct, rarityRu, slotLabel, unmetOf, unmetText, CATEGORY_RU } from '../common'
import './hero.css'

type Tab = 'gear' | 'stats'
const TABS = [{ id: 'gear', label: 'Снаряжение' }, { id: 'stats', label: 'Свойства' }]
const ZERO: Attributes = { str: 0, vit: 0, dex: 0, wis: 0, lck: 0, kno: 0 }

/** `/play/hero`: the doll of 12 slots, picking an item into a slot, attributes and derived stats. */
export function HeroScreen() {
  useShellMode('window')
  const [params, setParams] = useSearchParams()
  const tab: Tab = params.get('tab') === 'stats' ? 'stats' : 'gear'
  const slotParam = params.get('slot') as Slot | null
  const loadout = useLoadoutQuery()
  const heroQ = useHeroQuery()
  // The hero snapshot is the freshest (commands upsert it); the loadout copy is the fallback.
  const hero = heroQ.data ?? loadout.data?.hero
  const phone = usePhone()
  const [slot, setSlot] = useState<Slot | null>(slotParam)
  useEffect(() => setSlot(slotParam), [slotParam])

  const choose = (s: Slot | null) => {
    setSlot(s)
    const next = new URLSearchParams(params)
    if (s) next.set('slot', s)
    else next.delete('slot')
    next.delete('tab')
    setParams(next, { replace: true })
  }
  const setTab = (t: string) => setParams(t === 'stats' ? { tab: 'stats' } : {}, { replace: true })

  return (
    <LocationWindow title="Персонаж" emblem="nav.hero" tabs={TABS} activeTab={tab} onTab={setTab} idBase="hero" bodyClassName="hero">
      {loadout.isLoading && <Loading />}
      {loadout.isError && !loadout.data && <Failure error={loadout.error} retry={() => void loadout.refetch()} what="Снаряжение" />}
      {loadout.data && hero && (
        <>
          <Doll loadout={loadout.data} hero={hero} selected={tab === 'gear' ? slot : null} onSelect={s => choose(s === slot ? null : s)} />
          {tab === 'gear' && !phone && (slot ? <Picker loadout={loadout.data} slot={slot} onClose={() => choose(null)} /> : <StatsSummary hero={hero} />)}
          {tab === 'gear' && phone && (
            <>
              <KeyStats hero={hero} />
              <Sheet open={Boolean(slot)} title={<>Ячейка <b>{slotLabel(slot)}</b></>} label={`Ячейка ${slotLabel(slot)}`} emblem="slot.weapon" onClose={() => choose(null)}>
                {slot && <Picker loadout={loadout.data} slot={slot} onClose={() => choose(null)} />}
              </Sheet>
            </>
          )}
          {tab === 'stats' && <StatsZone hero={hero} />}
        </>
      )}
    </LocationWindow>
  )
}

function Doll({ loadout, hero, selected, onSelect }: { loadout: Loadout; hero: Hero; selected: Slot | null; onSelect: (s: Slot) => void }) {
  const bySlot = new Map(loadout.slots.map(s => [s.slot, s]))
  const cell = (slot: Slot) => {
    const def = SLOTS.find(s => s.slot === slot)!
    const ls = bySlot.get(slot)
    const e = ls?.entry ?? null
    const unmet = e ? ls?.unmet ?? null : null
    return (
      <div className={cx('doll3__slot', slot.startsWith('accessory') && 'doll3__slot--acc')} key={slot}>
        <button type="button" onClick={() => onSelect(slot)} aria-pressed={selected === slot}
          aria-label={`${def.label}: ${e ? e.item.name : 'пусто'}${unmet ? `, не действует: ${unmetText(unmet, hero.attributes)}` : ''}`}
          title={unmet ? `Не действует: ${unmetText(unmet, hero.attributes)}` : undefined}
          className={cx('gslot gslot--s', e ? `is-filled ${rarityClass(e.item.rarity)}` : 'is-locked', selected === slot && 'is-selected', unmet && 'is-unmet')}>
          {e ? <ItemArt item={e.item} size={38} /> : <Emblem name={def.emblem} />}
        </button>
        <span className={cx('gslot__tag', e && 'is-gold')} aria-hidden="true">{slot.startsWith('accessory') ? '' : def.label}</span>
      </div>
    )
  }
  return (
    <div className="doll3">
      <div className="doll3__col">{(['weapon1', 'head', 'shoulder', 'body'] as Slot[]).map(cell)}</div>
      <div className="doll3__figure" aria-hidden="true">
        <div className="doll__arch" />
        {hero.portrait ? <img className="doll3__portrait" src={hero.portrait} alt="" /> : <Emblem name="nav.hero" className="doll3__bust" />}
        <div className="doll3__cap">{hero.name} · ур. {hero.level}<br />сила снаряжения {loadout.gear_score}</div>
      </div>
      <div className="doll3__col">{(['weapon2', 'gloves', 'belt', 'legs'] as Slot[]).map(cell)}</div>
      <div className="doll3__acc">
        {(['accessory1', 'accessory2', 'accessory3', 'accessory4'] as Slot[]).map(cell)}
        <span className="gslot__tag doll3__acclabel">Обереги · 4 ячейки</span>
        <Link className="profession-entry" to="/play/professions">Мирные мастерства →</Link>
      </div>
    </div>
  )
}

/** Items of the bag that fit a slot, compared with what is equipped there. */
function Picker({ loadout, slot, onClose }: { loadout: Loadout; slot: Slot; onClose: () => void }) {
  const inv = useInventoryQuery()
  const [equip] = useEquipMutation()
  const [unequip] = useUnequipMutation()
  const loadoutQ = useLoadoutQuery()
  const heroQ = useHeroQuery()
  const cmd = useCommand(() => { void loadoutQ.refetch(); void heroQ.refetch(); void inv.refetch() })
  const ls = loadout.slots.find(s => s.slot === slot) as LoadoutSlot
  const current = ls.entry
  const fits = useMemo(() => (inv.data?.entries ?? []).filter(e => e.item.kind === 'gear' && ls.categories.includes(e.item.slot_category)), [inv.data, ls.categories])
  const [pick, setPick] = useState<number | null>(null)
  useEffect(() => setPick(null), [slot])
  const chosen = fits.find(e => e.id === pick) ?? null
  const hero = heroQ.data ?? loadout.hero
  const chosenUnmet = chosen ? unmetOf(chosen.item, hero.attributes) : null
  const chosenLowLevel = Boolean(chosen && chosen.item.level_required > hero.level)
  const inCombat = hero.in_combat

  const dispatch = useAppDispatch()
  const doEquip = async (e: InventoryEntry) => {
    const r = await cmd.run(() => equip({ expected_version: hero.version, slot, entry_id: e.id }).unwrap())
    if (r) dispatch(ui.actions.notice(`Надето: ${e.item.name}`))
  }
  const doUnequip = async () => {
    const name = current?.item.name
    const r = await cmd.run(() => unequip({ expected_version: hero.version, slot }).unwrap())
    if (r && name) dispatch(ui.actions.notice(`Снято: ${name}`))
  }

  const def = SLOTS.find(s => s.slot === slot)!
  return (
    <div className="picker">
      <div className="picker__head">
        <Emblem name={def.emblem} />Ячейка <b>{def.label}</b> · {ls.categories.map(c => CATEGORY_RU[c] ?? c).join(', ')}
        <button type="button" className="linkbtn picker__close" onClick={onClose}>к свойствам</button>
      </div>
      {current && ls.unmet && <p className="note picker__unmet" role="status">«{current.item.name}» не действует: {unmetText(ls.unmet, hero.attributes)}</p>}
      {inv.isLoading && <Loading />}
      {inv.isError && !inv.data && <Failure error={inv.error} retry={() => void inv.refetch()} what="Сума" />}
      {inv.data && (
        <ul className="plist" aria-label="Подходящие предметы">
          {fits.length === 0 && <li className="prow prow--empty">В суме нет подходящих предметов</li>}
          {fits.map(e => {
            const delta = modDiff(current?.item.modifiers, e.item.modifiers)[0]
            const lowLevel = e.item.level_required > hero.level
            const unmet = unmetOf(e.item, hero.attributes)
            const low = lowLevel || Boolean(unmet)
            return (
              <li key={e.id} className="srow">
                <button type="button" className={cx('prow', rarityClass(e.item.rarity), pick === e.id && 'is-selected', low && 'is-low')} aria-pressed={pick === e.id} onClick={() => setPick(e.id)}
                  onDoubleClick={() => { if (!inCombat && (e.equipped_in === slot || !low)) void (e.equipped_in === slot ? doUnequip() : doEquip(e)) }}>
                  <ItemArt item={e.item} size={34} />
                  <span className="name">{e.item.name}<small>{rarityRu(e.item.rarity)} · ур. {e.item.level_required || 1}{lowLevel ? ' · нужен уровень выше' : unmet ? ` · ${unmetText(unmet, hero.attributes)}` : ''}</small></span>
                  {e.equipped_in && <span className="tag">{e.equipped_in === slot ? 'надето' : slotLabel(e.equipped_in)}</span>}
                  {delta && e.equipped_in !== slot && <span className={cx('mod', delta.delta < 0 && 'down')}>{delta.label} {delta.delta > 0 ? '+' : '−'}{Math.abs(delta.delta)}</span>}
                </button>
                {e.equipped_in === slot
                  ? <PriceButton className="pricebtn--no" locked={inCombat} onPress={doUnequip} aria-label={`Снять: ${e.item.name}`}>Снять</PriceButton>
                  : <PriceButton locked={inCombat || low} onPress={() => doEquip(e)} aria-label={`Надеть: ${e.item.name}`}>Надеть</PriceButton>}
              </li>
            )
          })}
        </ul>
      )}
      {chosen && (
        <div className={cx('itemcard parch', rarityClass(chosen.item.rarity))}>
          <div className="itemcard__head">
            <ItemArt item={chosen.item} size={58} />
            <div><b>{chosen.item.name}</b><span className="rarity-line">{rarityRu(chosen.item.rarity)} · уровень {chosen.item.level_required || 1}</span></div>
          </div>
          <ResourceBonusNote modifiers={chosen.item.modifiers} />
          <div className="mods">
            {chosen.equipped_in === slot
              ? modList(chosen.item.modifiers).map(m => <Mod key={m.key} label={m.label} text={m.text} />)
              : modDiff(current?.item.modifiers, chosen.item.modifiers).map(d => (
                  <Mod key={d.key} label={d.label} text={d.key === 'crit_chance_bp' ? `${pct(d.from)} → ${pct(d.to)}` : `${d.from} → ${d.to}`} down={d.delta < 0} />
                ))}
            {gearFacts(chosen.item, hero.attributes).map(f => <Mod key={f.key} label={f.label} text={f.text} down={f.down} />)}
          </div>
          {current && chosen.equipped_in !== slot && <p className="note itemcard__note">Сейчас надето: {current.item.name}</p>}
          {slot === 'weapon1' && chosen.item.hands === 2 && chosen.equipped_in !== slot && loadout.slots.find(x => x.slot === 'weapon2')?.entry && (
            <p className="note itemcard__note">Двуручное: вторая рука освободится.</p>
          )}
          <div className="actions">
            {chosen.equipped_in === slot
              ? <ActionButton locked={inCombat} onPress={doUnequip}>Снять</ActionButton>
              : <ActionButton gold locked={inCombat || chosenLowLevel || Boolean(chosenUnmet)} onPress={() => doEquip(chosen)}>{chosenLowLevel ? `Нужен уровень ${chosen.item.level_required}` : chosenUnmet ? `Не надеть: ${unmetText(chosenUnmet, hero.attributes)}` : 'Надеть'}</ActionButton>}
          </div>
        </div>
      )}
      {!chosen && current && (
        <div className="actions picker__current">
          <ActionButton locked={inCombat} onPress={doUnequip}>Снять «{current.item.name}»</ActionButton>
        </div>
      )}
      {cmd.text && <p className="hero__error" role="alert">{cmd.text}</p>}
    </div>
  )
}

function Mod({ label, text, down }: { label: string; text: string; down?: boolean }) {
  return <><span>{label}</span><u className={down ? 'down' : undefined}>{text}</u></>
}

function resource(hero: Hero, kind: 'hp' | 'mn' | 'st') {
  return hero.resources.find(r => r.kind === kind)
}

function KeyStats({ hero }: { hero: Hero }) {
  const hp = resource(hero, 'hp')
  return (
    <div className="keystats">
      <div className="slab"><b><Emblem name="res.hp" />{hp?.value ?? '—'}</b>из {hp?.max ?? '—'}</div>
      <div className="slab"><b><Emblem name="stat.power" />{hero.derived.p}</b>удар</div>
      <div className="slab"><b><Emblem name="stat.armor" />{hero.derived.a}</b>броня</div>
      <div className="slab"><b><Emblem name="stat.crit" />{pct(hero.derived.crit_bp)}</b>крит</div>
    </div>
  )
}

function StatsSummary({ hero }: { hero: Hero }) {
  return (
    <div className="hero__side">
      <p className="note">Выберите ячейку на кукле, чтобы подобрать в неё предмет из сумы.</p>
      <Derived hero={hero} />
    </div>
  )
}

function Derived({ hero }: { hero: Hero }) {
  const faith = useFaithQuery()
  const god = faith.data?.gods.find(g => g.id === (faith.data?.current ?? hero.god_id))
  const res = (['hp', 'st', 'mn'] as const).map(k => ({ k, r: resource(hero, k) }))
  const RES = { hp: { label: 'Здоровье', emblem: 'res.hp' }, st: { label: 'Выносливость', emblem: 'res.stamina' }, mn: { label: 'Мана', emblem: 'res.mana' } } as const
  return (
    <div className="statcol">
      <div className="eyebrow">Состояние</div>
      {res.map(({ k, r }) => r && (
        <div className="stat stat--compact" key={k}><Emblem name={RES[k].emblem} />{RES[k].label}<b>{r.value}<small> / {r.max}</small></b></div>
      ))}
      {DERIVED_RU.map(d => (
        <div className="stat stat--compact" key={d.key}><Emblem name={d.emblem} />{d.label}<b>{d.bp ? pct(hero.derived[d.key]) : hero.derived[d.key]}</b></div>
      ))}
      <div className={cx('stat stat--compact', hero.derived.load > hero.derived.free_load && 'stat--warn')}
        title="Сверх свободной нагрузки: уклонение −0,4 % и сила духа −1 за очко">
        <Emblem name="slot.body" />Нагрузка<b>{hero.derived.load}<small> / {hero.derived.free_load}</small></b>
      </div>
      {god && (
        <>
          <div className="eyebrow hero__god">Покровитель: {god.name}</div>
          {modList(god.modifiers).map(m => <div className="stat stat--compact" key={m.key}>{m.label}<b>{m.text}</b></div>)}
        </>
      )}
    </div>
  )
}

/** Attributes: «+» fills a preview; «Применить» under them sends it. Reset asks in place and names the price. */
function StatsZone({ hero }: { hero: Hero }) {
  const [add, setAdd] = useState<Attributes>(ZERO)
  const [allocate] = useAllocateMutation()
  const [reset] = useResetAttributesMutation()
  const heroQ = useHeroQuery()
  const cmd = useCommand(() => void heroQ.refetch())
  useEffect(() => setAdd(ZERO), [hero.version])
  const spent = Object.values(add).reduce((a, b) => a + b, 0)
  const left = hero.free_points - spent
  const inCombat = hero.in_combat

  const apply = async () => {
    if (await cmd.run(() => allocate({ expected_version: hero.version, add }).unwrap())) setAdd(ZERO)
  }

  return (
    <div className="hero__side statcols">
      <div className="statcol">
        <div className="eyebrow">
          Свойства {hero.free_points > 0 && <Gem value={left} gold label={`свободно ${left}`} />}<span className="hero__free">{hero.free_points > 0 ? 'свободно' : 'свободных очков нет'}</span>
        </div>
        {ATTRS.map(a => {
          const base = hero.attributes[a.key]
          const effective = hero.effective_attributes[a.key]
          const bonus = effective - base
          const plus = add[a.key]
          const capped = base + plus >= hero.attribute_cap
          return (
            <div className="stat stat--compact hero__attribute" key={a.key}>
              <span className="hero__attr"><Emblem name={a.emblem} size={20} />{a.label}<small>{a.hint}</small></span>
              <b className="hero__attr-value">
                <span>{effective}{plus > 0 && <i> +{plus}</i>}</span>
              </b>
              <button type="button" className="rivet" aria-label={`Добавить очко: ${a.label}`} disabled={left <= 0 || capped || inCombat}
                onClick={() => setAdd(v => ({ ...v, [a.key]: v[a.key] + 1 }))}>+</button>
              <button type="button" className="rivet rivet--minus" aria-label={`Убрать очко: ${a.label}`} disabled={plus <= 0}
                onClick={() => setAdd(v => ({ ...v, [a.key]: v[a.key] - 1 }))}>−</button>
              {bonus !== 0 && <small className="hero__attr-bonus">{base} своё · {bonus > 0 ? '+' : ''}{bonus} бонус</small>}
            </div>
          )
        })}
        <p className="note">Предел свойства — {hero.attribute_cap}.</p>
        {spent > 0 && (
          <div className="actions hero__apply">
            <ActionButton gold locked={inCombat} onPress={apply}>Применить · {spent} из {hero.free_points}</ActionButton>
            <PlankButton onClick={() => setAdd(ZERO)}>Отменить</PlankButton>
          </div>
        )}
        <div className="hero__reset">
          <ConfirmInPlace question={<>Сбросить все вложенные очки? {hero.free_reset_left ? 'Первый сброс бесплатный.' : <>Цена — <b>{hero.reset_price}</b> серебра, у вас {hero.silver}.</>}</>}
            yes="Сбросить" no="Оставить" onConfirm={() => cmd.run(() => reset({ expected_version: hero.version }).unwrap())}>
            {ask => <PlankButton onClick={ask} disabled={inCombat}>Сбросить распределение</PlankButton>}
          </ConfirmInPlace>
        </div>
        {cmd.text && <p className="hero__error" role="alert">{cmd.text}</p>}
      </div>
      <Derived hero={hero} />
    </div>
  )
}
